import { VideoService } from '@/app/services/video/video.service';
import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { VideoThumbnailComponent } from '@/app/atoms/video-thumbnail/video-thumbnail.component';
import { FormatService } from '@/app/services/format/format.service';
import { VideoAiModerateService } from '@/app/services/video-ai-moderate/video-ai-moderate.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { Subscription, take } from 'rxjs';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';

@Component({
  selector: 'ins-video-moderation',
  imports: [VideoThumbnailComponent, InfiniteScrollDirective],
  templateUrl: './video-moderation.html',
  styleUrl: './video-moderation.scss',
})
export class VideoModeration implements OnInit, OnDestroy {

  pageNumber = signal<number>(0);
  take = signal<number>(10);
  hasReview = signal<-1 | 0 | 1>(0);
  hasMore = signal<boolean>(true);
  isLoadingMore = signal<boolean>(false);

  videoAiModerationInsights = signal<any[] | undefined>(undefined);

  currentlyProcessing = signal<string[]>([]);

  private subscriptionList = new Subscription();

  /** Services */
  private videoService = inject(VideoService);
  public formatService = inject(FormatService);
  private videoAiModerateService = inject(VideoAiModerateService);
  private toastNotificationService = inject(ToastNotificationService);
  /** Services - END */

  ngOnInit(): void {
    this.getVideoAiModerationInsights();
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  getVideoAiModerationInsights(append = false): void {
    if (append) {
      if (this.isLoadingMore() || !this.hasMore()) {
        return;
      }
      this.isLoadingMore.set(true);
    }

    this.subscriptionList.add(
      this.videoService
        .getVideoAiModerationInsights(this.hasReview(), this.take(), this.pageNumber() * this.take())
        .subscribe({
          next: (res: any[]) => {
            const chunk = res ?? [];

            if (append && this.videoAiModerationInsights()) {
              const existing = this.videoAiModerationInsights()!;
              this.videoAiModerationInsights.set([
                ...existing,
                ...chunk.filter((video) => !existing.some((item) => item.id === video.id)),
              ]);
            } else {
              this.videoAiModerationInsights.set(chunk);
            }

            this.hasMore.set(chunk.length >= this.take());
            this.isLoadingMore.set(false);
          },
          error: () => {
            this.isLoadingMore.set(false);
            if (append) {
              this.pageNumber.update((page) => Math.max(0, page - 1));
            } else {
              this.videoAiModerationInsights.set([]);
              this.hasMore.set(false);
            }
          },
        }),
    );
  }

  onScrollDown(): void {
    if (this.isLoadingMore() || !this.hasMore()) {
      return;
    }

    this.pageNumber.update((page) => page + 1);
    this.getVideoAiModerationInsights(true);
  }

  startProcessing(videoId: string) {
    this.currentlyProcessing.update(current => [...current, videoId]);
    this.videoAiModerateService.retryContentReview(videoId).pipe(take(1)).subscribe(async (response: any) => {
      console.log('Re-Try Sent Successfully: ', response);
      this.currentlyProcessing.update(current => current.filter(id => id !== videoId));
      await this.toastNotificationService.alert({
        title: 'Analysis Submitted!',
        message: 'Please check back later for the results.',
        type: 'success',
        icon: 'success'
      })
    }, async error => {
      console.error('Error retrying review: ', error);
      this.currentlyProcessing.update(current => current.filter(id => id !== videoId));
      await this.toastNotificationService.alert({
        title: 'Error Re-Running Analysis!',
        message: 'Something went wrong. If the problem persists, please contact support.',
        type: 'error',
        icon: 'error'
      })
    });
  }

  selectReviewState(state: -1 | 0 | 1) {
    this.hasReview.set(state);
    this.pageNumber.set(0);
    this.hasMore.set(true);
    this.isLoadingMore.set(false);
    this.videoAiModerationInsights.set(undefined);
    this.getVideoAiModerationInsights();
  }

  getTabClass(tab: -1 | 0 | 1) {
    return this.hasReview() === tab
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  copyBriefId(briefId: string) {
    navigator.clipboard.writeText(briefId);
    this.toastNotificationService.alert({
      title: 'Copied!',
      message: 'Brief ID copied to clipboard!',
      type: 'success',
      icon: 'success'
    });
  }
}
